import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    addDays,
    addMonths,
    addQuarters,
    addWeeks,
    addYears,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    format,
    isAfter,
    isValid,
    parseISO,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
} from 'date-fns';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import { RootState } from 'redux/store';

const formatDate = (d: Date) => format(d, 'yyyy-MM-dd');

const parseDateParam = (value: string | null): Date | null => {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) && formatDate(parsed) === value ? parsed : null;
};

export type AnalyticsIntervalKey =
    | 'week'
    | 'two_weeks'
    | 'month'
    | 'quarter'
    | 'six_months'
    | 'year';

export type AnalyticsDateRangeKey = '7' | '30' | '90' | 'custom';

export interface AnalyticsIntervalOption {
    key: AnalyticsIntervalKey;
    label: string;
}

export const ANALYTICS_INTERVAL_OPTIONS: AnalyticsIntervalOption[] = [
    { key: 'week', label: 'Week' },
    { key: 'two_weeks', label: '2 Weeks' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'six_months', label: '6 Months' },
    { key: 'year', label: 'Year' },
];

// Week starts on Monday to match how the rest of the analytics surfaces talk
// about "this week" (Mon–Sun).
const WEEK_OPTIONS = { weekStartsOn: 1 as const };

const computeIntervalRange = (
    key: AnalyticsIntervalKey,
    anchor: Date
): { from: Date; to: Date } => {
    switch (key) {
        case 'week':
            return {
                from: startOfWeek(anchor, WEEK_OPTIONS),
                to: endOfWeek(anchor, WEEK_OPTIONS),
            };
        case 'two_weeks': {
            const start = startOfWeek(anchor, WEEK_OPTIONS);
            return {
                from: start,
                to: endOfWeek(addDays(start, 7), WEEK_OPTIONS),
            };
        }
        case 'month':
            return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
        case 'quarter':
            return { from: startOfQuarter(anchor), to: endOfQuarter(anchor) };
        case 'six_months':
            return {
                from: startOfMonth(subMonths(anchor, 5)),
                to: endOfMonth(anchor),
            };
        case 'year':
            return { from: startOfYear(anchor), to: endOfYear(anchor) };
        default:
            return { from: anchor, to: anchor };
    }
};

const shiftAnchor = (key: AnalyticsIntervalKey, anchor: Date, direction: -1 | 1): Date => {
    switch (key) {
        case 'week':
            return addWeeks(anchor, direction);
        case 'two_weeks':
            return addWeeks(anchor, direction * 2);
        case 'month':
            return addMonths(anchor, direction);
        case 'quarter':
            return addQuarters(anchor, direction);
        case 'six_months':
            return addMonths(anchor, direction * 6);
        case 'year':
            return addYears(anchor, direction);
        default:
            return anchor;
    }
};

export interface AnalyticsFiltersState {
    fromDate: Date | null;
    toDate: Date | null;
    /** Raw form-control value; '' when nothing is selected. */
    serviceProviderId: string;
    setFromDate: (d: Date | null) => void;
    setToDate: (d: Date | null) => void;
    setServiceProviderId: (id: string) => void;
    hasValidDateRange: boolean;
    /** Shared 7/30/90/custom range used by the Analytics header and Provider Activity. */
    dateRangeKey: AnalyticsDateRangeKey;
    setDateRangeKey: (key: AnalyticsDateRangeKey) => void;
    /** Formatted yyyy-MM-dd, undefined when range is invalid/missing. */
    from?: string;
    /** Formatted yyyy-MM-dd, undefined when range is invalid/missing. */
    to?: string;
    /** Normalized service provider scope, undefined when none selected. */
    serviceProviderIdScope?: string;
    /** Currently selected interval preset, or null for a custom range. */
    intervalKey: AnalyticsIntervalKey | null;
    /** Picks a preset and snaps the dates to its current period. */
    setIntervalKey: (key: AnalyticsIntervalKey | null) => void;
    /** Steps the current range backward (−1) or forward (+1) by one interval. */
    shiftInterval: (direction: -1 | 1) => void;
}

/**
 * Centralized analytics header filters (date range + service provider).
 *
 * Owns the controlled state that drives both the report download and the
 * surface-level data fetches (Documents Uploaded, Messages Posted, etc).
 *
 * For non-SUPER_SUPER_ADMIN users the service provider is auto-populated
 * from the user's own profile.
 */
const useAnalyticsFilters = (): AnalyticsFiltersState => {
    const { role } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const userServiceProviderId = useSelector(
        (state: RootState) => state.user.entity.service_provider_id
    );
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const initialFromParam = parseDateParam(searchParams.get('from'));
    const initialToParam = parseDateParam(searchParams.get('to'));
    const hasInitialUrlRange = Boolean(
        initialFromParam && initialToParam && !isAfter(initialFromParam, initialToParam)
    );

    const [fromDate, setFromDateState] = useState<Date | null>(() =>
        hasInitialUrlRange ? initialFromParam : subDays(new Date(), 29)
    );
    const [toDate, setToDateState] = useState<Date | null>(() =>
        hasInitialUrlRange ? initialToParam : new Date()
    );
    const [serviceProviderId, setServiceProviderId] = useState('');
    const [dateRangeKey, setDateRangeKeyState] = useState<AnalyticsDateRangeKey>(
        hasInitialUrlRange ? 'custom' : '30'
    );
    const [intervalKey, setIntervalKeyState] = useState<AnalyticsIntervalKey | null>(null);
    const [intervalAnchor, setIntervalAnchor] = useState<Date | null>(null);

    useEffect(() => {
        if (!isSuperSuperAdmin) {
            setServiceProviderId(userServiceProviderId ? String(userServiceProviderId) : '');
        }
    }, [isSuperSuperAdmin, userServiceProviderId]);

    const setDateParams = useCallback(
        (nextFrom: Date | null, nextTo: Date | null) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    if (nextFrom && isValid(nextFrom)) {
                        next.set('from', formatDate(nextFrom));
                    } else {
                        next.delete('from');
                    }
                    if (nextTo && isValid(nextTo)) {
                        next.set('to', formatDate(nextTo));
                    } else {
                        next.delete('to');
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // Write the default range before passive effects. setSearchParams navigates to
    // "?from&to" using this render's pathname, so doing it in useEffect runs
    // after a child <Navigate> and replaces that redirect. The overview then
    // never mounts, and Summary & Provider Activity stay empty until refresh.
    useLayoutEffect(() => {
        if (
            fromDate &&
            toDate &&
            (!parseDateParam(searchParams.get('from')) || !parseDateParam(searchParams.get('to')))
        ) {
            setDateParams(fromDate, toDate);
        }
    }, [fromDate, searchParams, setDateParams, toDate]);

    // Manual date edits drop us back to a "custom" range so the prev/next
    // navigation stops snapping the user's hand-picked dates around.
    const setFromDate = useCallback(
        (d: Date | null) => {
            setFromDateState(d);
            setDateParams(d, toDate);
            setDateRangeKeyState('custom');
            setIntervalKeyState(null);
            setIntervalAnchor(null);
        },
        [setDateParams, toDate]
    );

    const setToDate = useCallback(
        (d: Date | null) => {
            setToDateState(d);
            setDateParams(fromDate, d);
            setDateRangeKeyState('custom');
            setIntervalKeyState(null);
            setIntervalAnchor(null);
        },
        [fromDate, setDateParams]
    );

    const setDateRangeKey = useCallback(
        (key: AnalyticsDateRangeKey) => {
            setDateRangeKeyState(key);
            if (key === 'custom') return;

            const days = Number(key);
            const today = new Date();
            const nextFrom = subDays(today, days - 1);
            setFromDateState(nextFrom);
            setToDateState(today);
            setDateParams(nextFrom, today);
        },
        [setDateParams]
    );

    const setIntervalKey = useCallback(
        (key: AnalyticsIntervalKey | null) => {
            if (!key) {
                setIntervalKeyState(null);
                setIntervalAnchor(null);
                return;
            }
            const anchor = new Date();
            const { from: nextFrom, to: nextTo } = computeIntervalRange(key, anchor);
            setIntervalAnchor(anchor);
            setIntervalKeyState(key);
            setFromDateState(nextFrom);
            setToDateState(nextTo);
            setDateParams(nextFrom, nextTo);
        },
        [setDateParams]
    );

    const shiftInterval = useCallback(
        (direction: -1 | 1) => {
            if (!intervalKey || !intervalAnchor) return;
            const newAnchor = shiftAnchor(intervalKey, intervalAnchor, direction);
            const { from: nextFrom, to: nextTo } = computeIntervalRange(intervalKey, newAnchor);
            setIntervalAnchor(newAnchor);
            setFromDateState(nextFrom);
            setToDateState(nextTo);
            setDateParams(nextFrom, nextTo);
        },
        [intervalKey, intervalAnchor, setDateParams]
    );

    const hasValidDateRange = useMemo(
        () =>
            !!(
                fromDate &&
                toDate &&
                isValid(fromDate) &&
                isValid(toDate) &&
                !isAfter(fromDate, toDate)
            ),
        [fromDate, toDate]
    );

    const from = hasValidDateRange && fromDate ? formatDate(fromDate) : undefined;
    const to = hasValidDateRange && toDate ? formatDate(toDate) : undefined;
    const serviceProviderIdScope = serviceProviderId || undefined;

    return {
        fromDate,
        toDate,
        serviceProviderId,
        setFromDate,
        setToDate,
        setServiceProviderId,
        hasValidDateRange,
        dateRangeKey,
        setDateRangeKey,
        from,
        to,
        serviceProviderIdScope,
        intervalKey,
        setIntervalKey,
        shiftInterval,
    };
};

export default useAnalyticsFilters;
